import React from "react";
import { SectionLabel } from "./Brand";

/**
 * Full Buyer Conviction Sprint brief — rendered inline so candidates
 * always see the canonical assignment inside the app.
 */
export default function AssignmentBrief() {
  return (
    <article className="bh-prose" data-testid="assignment-brief">
      <SectionLabel className="mb-3">The Brief</SectionLabel>
      <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white">
        Hiring Challenge: Buyer Conviction Sprint
      </h1>
      <p className="text-zinc-300 text-base leading-relaxed mt-4">
        Some companies have strong products but weak web presence. A weak web
        presence can reduce trust, create confusion, and lower the chances of
        enquiries, demos, or serious sales conversations. Your task is to
        analyse one real B2B website, identify where buyer trust may be
        breaking, and build a simple audit system Brandhero could use again.
      </p>

      <h2>Case: Business Analysis — MVS Valves</h2>
      <p>
        <strong>Website:</strong>{" "}
        <a href="https://mvsvalves.com" target="_blank" rel="noreferrer">
          https://mvsvalves.com
        </a>
        <br />
        <strong>Category:</strong> Industrial manufacturing / engineering / B2B
        exports
      </p>
      <p>
        MVS Valves is an industrial manufacturing company that supplies valves,
        lined valves, thermoplastic valves, pipes, fittings, and related
        industrial products. Your job is to analyse the website from the point
        of view of a serious B2B buyer.
      </p>

      <h2>Timeline</h2>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Challenge window</td>
            <td>4 hours 30 minutes (in-app timer)</td>
          </tr>
          <tr>
            <td>Expected effort</td>
            <td>3–4 focused hours</td>
          </tr>
          <tr>
            <td>Submission format</td>
            <td>This portal — the "Submit" tab</td>
          </tr>
          <tr>
            <td>Shortlisting for interview</td>
            <td>Based on submission quality</td>
          </tr>
        </tbody>
      </table>

      <h2>Tools Allowed</h2>
      <p>You may use:</p>
      <ul>
        <li>ChatGPT, Claude, Perplexity, Gemini</li>
        <li>Google Sheets, Notion, Airtable</li>
        <li>Canva, Lovable, Bolt, Replit</li>
        <li>Any other useful tool</li>
      </ul>
      <p>
        <strong>Mention all tools used in your final submission.</strong>
      </p>

      <h2>Final Deliverables</h2>
      <p>
        Submit the following neatly organised in an online{" "}
        <strong>Google Doc</strong> (not MS Word) and/or a{" "}
        <strong>Google Sheet</strong> (not MS Excel):
      </p>
      <ol>
        <li>Website / brand diagnosis</li>
        <li>Buyer Conviction Score</li>
        <li>Google Sheet audit system</li>
        <li>Outreach message</li>
        <li>Walkthrough video</li>
        <li>Self-starter answers</li>
      </ol>

      <h2>Part A · Website Diagnosis</h2>
      <p>
        Study the MVS Valves website carefully. Review the homepage, about
        page, product pages, contact / enquiry flow, and any catalogue,
        certification, quality, or technical information available.
      </p>

      <h3>1. Company Summary</h3>
      <p>What does MVS Valves do? Write in simple language. Max 5–6 lines.</p>

      <h3>2. Target Buyer</h3>
      <p>
        Who is the likely buyer? Mention buyer role, industry, company type,
        main requirement, and what they would need to trust before sending an
        enquiry. Max 8–10 lines.
      </p>

      <h3>3. Main Website Goal</h3>
      <p>
        What should the website make the buyer do? Choose one primary action:
        send an enquiry, request a quote, download catalogue, call the company,
        share technical requirements, or start a procurement conversation.
        Explain whether the current website makes this action easy. Max 5–6
        lines.
      </p>

      <h3>4. Top 5 Trust / Clarity Gaps</h3>
      <p>Identify the 5 biggest issues that may reduce buyer confidence.</p>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Gap</th>
            <th>What You Noticed</th>
            <th>Why It Matters</th>
            <th>Business Impact</th>
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5].map((i) => (
            <tr key={i}>
              <td>{i}</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        Look for issues related to: product clarity, technical information,
        certifications, quality proof, export readiness, industry applications,
        catalogue or datasheet access, contact / enquiry flow, visual trust,
        buyer confidence.
      </p>

      <h3>5. Top 3 Fixes</h3>
      <p>Choose the 3 highest-impact improvements.</p>
      <table>
        <thead>
          <tr>
            <th>Priority</th>
            <th>Recommended Fix</th>
            <th>Why This Matters</th>
            <th>Expected Business Impact</th>
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3].map((i) => (
            <tr key={i}>
              <td>{i}</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Part B · Buyer Conviction Score</h2>
      <p>Score the MVS Valves website using the table below.</p>
      <table>
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Score 1–5</th>
            <th>Evidence From Website</th>
            <th>Suggested Fix</th>
          </tr>
        </thead>
        <tbody>
          {[
            "Offer clarity",
            "Buyer relevance",
            "Product clarity",
            "Technical depth",
            "Trust and credibility",
            "Certification / quality proof",
            "Industry / application clarity",
            "CTA / enquiry clarity",
            "Visual professionalism",
            "Speed of understanding",
          ].map((p) => (
            <tr key={p}>
              <td>{p}</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4>Scoring guide</h4>
      <table>
        <thead>
          <tr>
            <th>Score</th>
            <th>Meaning</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>1</td><td>Weak</td></tr>
          <tr><td>2</td><td>Below average</td></tr>
          <tr><td>3</td><td>Acceptable</td></tr>
          <tr><td>4</td><td>Strong</td></tr>
          <tr><td>5</td><td>Excellent</td></tr>
        </tbody>
      </table>

      <h4>Final Score</h4>
      <p>Total Score: ___ / 50</p>

      <h4>One-line diagnosis</h4>
      <p>Write one sharp sentence summarising the website's biggest business issue.</p>
      <blockquote>
        The website shows industrial capability but does not build enough buyer
        confidence quickly enough for serious procurement enquiries.
      </blockquote>

      <h2>Part C · Google Sheet Audit System</h2>
      <p>
        Build a Google Sheet that Brandhero could use to audit industrial B2B
        websites. Your sheet should include:
      </p>
      <ul>
        <li>Company name, Website URL, Industry, Buyer type</li>
        <li>Main conversion goal</li>
        <li>10 audit parameters with score for each, total /50</li>
        <li>Priority level, top trust gap, top recommended fix</li>
        <li>Suggested Brandhero pitch angle &amp; recommended next action</li>
      </ul>

      <h4>Required sheet tabs</h4>
      <ol>
        <li>
          <strong>Website Audit</strong> — your completed audit for MVS Valves.
        </li>
        <li>
          <strong>Scoring Framework</strong> — what each score means.
        </li>
        <li>
          <strong>Brandhero Pitch Angle</strong> — convert your audit into a
          business angle (best angle, main buyer pain, strongest website issue,
          commercial risk, recommended first project, why this company may
          need Brandhero).
        </li>
      </ol>

      <h4>Bonus</h4>
      <ul>
        <li>Auto-calculated score</li>
        <li>Priority labels &amp; colour coding</li>
        <li>Simple dashboard</li>
        <li>AI prompt included inside the sheet</li>
        <li>Clear instructions for future use</li>
      </ul>

      <h2>Part D · Outreach Message</h2>
      <h4>LinkedIn DM</h4>
      <p>
        Max 500 characters. Make it specific. Avoid generic lines like “We
        provide website design services.” Use the actual insight from your
        audit.
      </p>
      <h4>Email subject line</h4>
      <p>
        Write one. Example:{" "}
        <em>Quick website trust gaps noticed for MVS Valves</em>.
      </p>
      <h4>Short insight message</h4>
      <p>
        Write 4–6 lines explaining the main issue and why it matters. The
        message should be useful even if the company does not hire Brandhero.
      </p>

      <h2>Part E · Loom / Screen-recording Video</h2>
      <p>
        Record a short Loom / screen recording explaining your submission.{" "}
        <strong>Your face should be visible.</strong>
      </p>
      <p>
        <strong>Max length:</strong> 90–120 seconds. Cover:
      </p>
      <ul>
        <li>What MVS Valves does</li>
        <li>Who the likely buyer is</li>
        <li>Biggest trust gap you found</li>
        <li>Top fix you recommend</li>
        <li>Google Sheet system you built</li>
        <li>AI / tools used</li>
        <li>What you would improve next</li>
      </ul>

      <h2>Part F · Self-Starter Answers</h2>
      <ol>
        <li>What is one insight you found that was not directly asked?</li>
        <li>
          If Brandhero gave you 20 similar websites to audit, how would you
          make the process faster?
        </li>
        <li>What part of this challenge was unclear, and how did you handle it?</li>
        <li>What would you improve if you had one more day?</li>
        <li>Where did you use AI, and where did you use your own judgment?</li>
      </ol>

      <h2>Submission Checklist</h2>
      <ul>
        <li>Website diagnosis completed</li>
        <li>Buyer Conviction Score completed</li>
        <li>Google Sheet audit system created (optional but recommended)</li>
        <li>Outreach message written</li>
        <li>Walkthrough video uploaded with face visible</li>
        <li>Self-starter answers completed</li>
        <li>AI / tools used mentioned</li>
        <li>All links accessible (anyone with link can view)</li>
      </ul>

      <h2>What Strong Submissions Show</h2>
      <ul>
        <li>Clear explanation &amp; buyer-first thinking</li>
        <li>Specific website observations with evidence</li>
        <li>Practical recommendations</li>
        <li>Simple but useful Google Sheet system</li>
        <li>Smart AI usage &amp; honest judgment</li>
        <li>Clear communication &amp; ownership beyond the basic task</li>
      </ul>

      <h2>Final Note</h2>
      <p>
        Brandhero is looking for people who can think clearly, learn quickly,
        use tools intelligently, and create useful output from limited
        information. <strong>Perfect answers are not expected.</strong> Clear
        thinking and useful execution are.
      </p>

      <div className="mt-10 border border-[var(--bh-accent-border)] bg-[var(--bh-accent-soft)] p-5 rounded-sm">
        <SectionLabel className="mb-2">Heads-up</SectionLabel>
        <p className="text-sm text-zinc-200 leading-relaxed">
          When you're ready to submit, switch to the{" "}
          <strong className="text-white">Submit</strong> tab above. Save your
          draft anytime; the system auto-saves every 30 seconds.
        </p>
      </div>
    </article>
  );
}
